import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { getDaysInMonth, format } from 'date-fns';
import { FutureHistoryTradeService } from '@pg-app/client/client-dashboard/components/future-history-trade/services/future-history-trade.service';
import { DownloadHistoryTradeComponent } from '@pg-app/client/client-dashboard/components/tables/download-history-trade/download-history-trade.component';
import { SharingDataService } from '@pg-app/client/client-dashboard/components/tables/sharing-data.service';
import { HistoryTradeDetailComponent } from '@pg-app/client/client-dashboard/components/tables/history-trade-detail/history-trade-detail.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Router } from '@angular/router';

@Component({
  selector: 'np-future-history-trade',
  templateUrl: './future-history-trade.component.html',
  styleUrls: ['./future-history-trade.component.scss']
})
export class FutureHistoryTradeComponent implements OnInit {
  chooseDate: FormGroup;
  monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  today = new Date();
  chartData = [];
  xAxisLabel = 'Months';
  yAxisLabel = 'No. of trades';
  colorScheme = {
    domain: ['#a5b3bf', '#011e41']
  };
  barPadding = 8;

  get getstartHistoryCtrl(): FormControl {
    return this.chooseDate.get('pgstartDateHistory') as FormControl;
  }
  get getendHistoryCtrl(): FormControl {
    return this.chooseDate.get('pgendDateHistory') as FormControl;
  }

  constructor(
    private futureHistoryTradeService: FutureHistoryTradeService,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private sharingDataService: SharingDataService,
    private breakpointObserver: BreakpointObserver,
    private router: Router
  ) { }

  ngOnInit() {
    this.chooseDate = this.fb.group({
      pgstartDateHistory: '',
      pgendDateHistory: ''
    });

    this.getendHistoryCtrl.setValue(this.today);
    const dateHistoryDefault = new Date(this.today.getFullYear() - 1, this.today.getMonth() + 1, 1);
    this.getstartHistoryCtrl.setValue(dateHistoryDefault);

    this.sharingDataService.startDate = this.getstartHistoryCtrl.value;
    this.sharingDataService.endDate = this.getendHistoryCtrl.value;

    this.getHistoryChartData();
    this.breakpointObserver
      .observe([Breakpoints.XLarge])
      .subscribe(result => {
        this.barPadding = result.matches ? 22 : 8;
      });
  }

  downloadFile() {
    this.dialog.open(DownloadHistoryTradeComponent, {
      disableClose: true
    });
  }

  startDateHistoryChange() {
    this.sharingDataService.startDate = this.getstartHistoryCtrl.value;
    const x =
      this.today < this.changeStartDate(this.getstartHistoryCtrl.value, this.getendHistoryCtrl.value)
        ? this.today
        : this.changeStartDate(this.getstartHistoryCtrl.value, this.getendHistoryCtrl.value);
    this.getendHistoryCtrl.setValue(x);
    this.getHistoryChartData();
  }

  endDateHistoryChange() {
    this.sharingDataService.endDate = this.getendHistoryCtrl.value;
    this.getstartHistoryCtrl.setValue(this.changeEndDate(this.getstartHistoryCtrl.value, this.getendHistoryCtrl.value));
    this.getHistoryChartData();
  }

  togglePicker(picker) {
    picker.opened ? picker.close() : picker.open();
  }

  public getHistoryChartData() {
    this.chartData = [];

    const chartData = {
      fromDate: format(this.getstartHistoryCtrl.value, 'YYYYMMDD'),
      toDate: format(this.getendHistoryCtrl.value, 'YYYYMMDD')
    };

    this.futureHistoryTradeService.getHistoryChartData(chartData).subscribe(
      data => {
        data = data || [];
        const array = new Array('GBP', 'EUR', 'USD');
        data = this.sort(data);

        data.forEach(item => {
          if (item.historyForCurrencies) {
            const outstandingsGBP = item.historyForCurrencies.find(p => p.state === 1 && array.indexOf(p.currency) === 0);
            const outstandingsEUR = item.historyForCurrencies.find(p => p.state === 1 && array.indexOf(p.currency) === 1);
            const outstandingsUSD = item.historyForCurrencies.find(p => p.state === 1 && array.indexOf(p.currency) === 2);
            const outstandingsOther = item.historyForCurrencies.filter(p => p.state === 1 && array.indexOf(p.currency) < 0);

            const settledsGBP = item.historyForCurrencies.find(p => p.state === 3 && array.indexOf(p.currency) === 0);
            const settledsEUR = item.historyForCurrencies.find(p => p.state === 3 && array.indexOf(p.currency) === 1);
            const settledsUSD = item.historyForCurrencies.find(p => p.state === 3 && array.indexOf(p.currency) === 2);
            const settledsOther = item.historyForCurrencies.filter(p => p.state === 3 && array.indexOf(p.currency) < 0);

            const dataCustom = {
              name: `${this.monthNames[item.month - 1]}`,
              series: [
                {
                  name: 'Settled',
                  value: item.totalNumbersSettled,
                  extra: {
                    gbp: settledsGBP ? settledsGBP.amount : 0,
                    eur: settledsEUR ? settledsEUR.amount : 0,
                    usd: settledsUSD ? settledsUSD.amount : 0,
                    other: settledsOther.length > 0 ? settledsOther.map(p => p.amount).reduce((a, b) => a + b, 0) : 0,
                    class: 'heather',
                    month: item.month,
                    year: item.year,
                  }
                },
                {
                  name: 'Outstanding',
                  value: item.totalNumbersOutstanding,
                  extra: {
                    gbp: outstandingsGBP ? outstandingsGBP.amount : 0,
                    eur: outstandingsEUR ? outstandingsEUR.amount : 0,
                    usd: outstandingsUSD ? outstandingsUSD.amount : 0,
                    other: outstandingsOther.length > 0 ? outstandingsOther.map(p => p.amount).reduce((a, b) => a + b, 0) : 0,
                    class: 'dark-blue',
                    month: item.month,
                    year: item.year,
                  }
                }
              ]
            };
            this.chartData = this.chartData.concat(dataCustom);
          } else {
            const dataCustom = {
              name: `${this.monthNames[item.month - 1]}`,
              month: item.month,
              year: item.year,
              series: [
                {
                  name: 'Settled',
                  value: 0,
                  extra: {
                    month: item.month,
                    year: item.year,
                  }
                },
                {
                  name: 'Outstanding',
                  value: 0,
                  extra: {
                    month: item.month,
                    year: item.year,
                  }
                }
              ],
            };
            this.chartData = this.chartData.concat(dataCustom);
          }
        });
      },
    );
  }

  private sort(data: any[]) {
    return data.sort((previous: any, current: any) => {
      let previousVal = previous['year'];
      let currentVal = current['year'];
      if (previousVal > currentVal) {
        return 1;
      } else if (previousVal < currentVal) {
        return -1;
      } else {
        previousVal = previous['month'];
        currentVal = current['month'];

        if (previousVal > currentVal) {
          return 1;
        } else if (previousVal < currentVal) {
          return -1;
        }
      }
      return 0;
    });
  }

  changeStartDate(startDate: Date, endDate: Date) {
    const dateDefault = new Date(startDate.getFullYear() + 1, startDate.getMonth() - 1, 1);
    const value = new Date(startDate.getFullYear() + 1, startDate.getMonth() - 1, getDaysInMonth(dateDefault));
    return value < endDate || endDate < startDate ? value : endDate;
  }

  changeEndDate(startDate: Date, endDate: Date) {
    const value = new Date(endDate.getFullYear() - 1, endDate.getMonth() + 1, 1);
    return value > startDate || endDate < startDate ? value : startDate;
  }

  onSelect(event) {
    const doc = document.documentElement;
    const left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

    if (top !== 0 || left !== 0) {
      window.scrollTo({
        top: 0,
        left: 0
      });
    }

    const  modal = this.dialog.open(HistoryTradeDetailComponent, {
        disableClose: true,
        minHeight: '80vh',
        minWidth: '80vw',
        data: {
          month: event.extra.month,
          state: event.name === 'Settled' ? 3 : 1,
          year: event.extra.year
        }
      });
    modal.afterClosed().subscribe(() => {
      if (top !== 0 || left !== 0) {
        window.scroll({
          top: top,
          left: left,
          behavior: 'smooth'
        });
      }
    });
  }

  navigateToTradeHistory() {
    this.router.navigate(['client/trade-history']);
  }
}
